import { Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import moment from 'moment'
import classNames from 'classnames'
import { DocumentDownloadIcon, DotsVerticalIcon, LinkIcon, PlusIcon, TruckIcon, UploadIcon, XIcon } from '@heroicons/react/outline'
import { get, trim, includes, forEach, isNil, toLower } from 'lodash'
import apiGet from 'lib/network/apiGet'

import { useHistory, useParams, useLocation } from 'react-router-dom'
import Switch from 'components/atoms/Switch'

import Selection from 'components/molecules/Selection'

import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import { startEndTruncate, accessibleOnClick } from 'utils'

import { IoCopyOutline, IoSearch, IoFilter, IoClose } from 'react-icons/io5'
import notify from 'components/atoms/Notify'
import useModal from 'hooks/useModal'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import { Dialog, Menu, Transition } from '@headlessui/react'

import ImportUsers from 'pages/company/modals/ImportUsers'
import CarAuth from './modals/CarAuth'
import { useSelector } from 'react-redux'
import PageFooter from 'components/paginationComponent'
import CarBooking from './modals/CarBooking'
import apiPut from 'lib/network/apiPut'
import apiGetBlob from 'lib/network/apiGetBlob'
import CompanyStatsSummary from 'components/company/CompanyStatsSummary'

const Cars = () => {
  const { t, i18n } = useTranslation()

  const RentalCarStatus = [
    { id: 'active', name: t('cars.status.active') },
    { id: 'booked', name: t('cars.status.booked') },
    { id: 'out of service', name: t('cars.status.outOfService') },
    { id: 'inProgress', name: t('cars.status.inProgress') },
  ]

  const modelYearOptions = useMemo(() => {
    const maxY = new Date().getFullYear() + 1
    const minY = 1980
    const years = []
    for (let y = maxY; y >= minY; y -= 1) {
      years.push({ id: String(y), name: String(y) })
    }
    return years
  }, [])

  const history = useHistory()
  const location = useLocation()

  const getBaseUrl = () => {
    const pathname = location.pathname
    const searchParams = new URLSearchParams(location.search)

    if (includes(pathname, 'dashboard-dev') || searchParams.has('dashboard-dev')) {
      return 'https://uat.ajarlee.om'
    }

    if (includes(pathname, 'dashboard') || searchParams.has('dashboard')) {
      return 'https://www.ajarlee.om'
    }

    return process.env.REACT_APP_FRONTEND_HOST_URL || ''
  }

  const getCarUrl = carId => {
    if (!carId) return null
    const baseUrl = getBaseUrl()
    return `${baseUrl}/car/${carId}/`
  }

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search)
    const rawYear = params.get('modelYear')
    let modelYear
    if (rawYear) {
      const y = parseInt(rawYear, 10)
      const maxY = new Date().getFullYear() + 1
      if (Number.isFinite(y) && y >= 1980 && y <= maxY) {
        modelYear = String(y)
      }
    }
    return {
      company: params.get('company') || undefined,
      branch: params.get('branch') || undefined,
      status: params.get('status') || undefined,
      fuelPolicy: params.get('fuelPolicy') || undefined,
      marketSegment: params.get('marketSegment') || undefined,
      insurance: params.get('insurance') || undefined,
      modelYear,
      showPendingImagesOnly: params.get('showPendingImagesOnly') === 'true',
      search: params.get('search') || '',
      sortBy: params.get('sortBy') || undefined,
      sortOrder: params.get('sortOrder') || undefined,
    }
  }

  const initialParams = getQueryParams()
  const [filter, setFilter] = useState({
    workspace: -1,
    company: initialParams.company,
    branch: initialParams.branch,
    status: initialParams.status,
    fuelPolicy: initialParams.fuelPolicy,
    marketSegment: initialParams.marketSegment,
    insurance: initialParams.insurance,
    modelYear: initialParams.modelYear,
    showPendingImagesOnly: initialParams.showPendingImagesOnly,
    sortBy: initialParams.sortBy,
    sortOrder: initialParams.sortOrder,
  })
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pagination, setPagination] = useState({})
  const [cars, setCars] = useState([])
  const [companyNames, setCompanyNames] = useState([])
  const [branchNames, setBranchNames] = useState([])
  const [search, setSearch] = useState(initialParams.search)
  const [selectedCarId, setSelectedCarId] = useState(null)
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [fuelPolicyOptions, setFuelPolicyOptions] = useState([])
  const [marketSegmentOptions, setMarketSegmentOptions] = useState([])
  const [insuranceOptions, setInsuranceOptions] = useState([])

  const { id } = useParams()
  const userFromStore = useSelector(state => get(state, 'user.user'))
  const companyBranchFilterId = useSelector(state => get(state, 'settings.companyBranchFilterId'))
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  const isSuperCompanyUser = userFromStore?.superCompanyUser

  // Check if current language is Arabic for RTL & localized values
  const isArabic = i18n.language === 'ar'
  const isRTL = isArabic
  const carStatusFilterTitle = isArabic ? t('cars.filters.status') : 'Car Status Filter'
  const companyTypeFilterTitle = t('cars.filters.companyTypeFilter') || 'Company Type Filter'
  const branchTypeFilterTitle = t('cars.filters.branchTypeFilter') || 'Branch Type Filter'
  const fuelPolicyFilterTitle = t('cars.filters.fuelPolicyFilter') || 'Fuel Policy Filter'
  const marketSegmentFilterTitle = t('cars.filters.marketSegmentFilter') || 'Market Segment Filter'
  const insuranceFilterTitle = t('cars.filters.insuranceFilter') || 'Insurance Filter'
  const modelYearFilterTitle = t('cars.filters.modelYear')

  const updateQueryParams = (newFilter, newSearch) => {
    const params = new URLSearchParams()
    if (newFilter.company) params.set('company', newFilter.company)
    if (newFilter.branch) params.set('branch', newFilter.branch)
    if (newFilter.status) params.set('status', newFilter.status)
    if (newFilter.fuelPolicy) params.set('fuelPolicy', newFilter.fuelPolicy)
    if (newFilter.marketSegment) params.set('marketSegment', newFilter.marketSegment)
    if (newFilter.insurance) params.set('insurance', newFilter.insurance)
    if (newFilter.modelYear) params.set('modelYear', newFilter.modelYear)
    if (newFilter.showPendingImagesOnly) params.set('showPendingImagesOnly', 'true')
    if (newSearch) params.set('search', newSearch)
    if (newFilter.sortBy && newFilter.sortOrder) {
      params.set('sortBy', newFilter.sortBy)
      params.set('sortOrder', newFilter.sortOrder)
    }
    history.replace({ pathname: '/cars', search: params.toString() })
  }

  useEffect(() => {
    updateQueryParams(filter, search)
    // eslint-disable-next-line
  }, [filter, search])

  useEffect(() => {
    fetchCars()
  }, [filter, search, companyBranchFilterId])

  const buildCarsParams = (page = 1, company = null, branch = null, status = null, includePagination = true) => {
    const params = {}

    if (includePagination) {
      params.pageNumber = page
      params.pageSize = 20
    }

    params.query = search || undefined
    if (filter.company || company) params.companyId = company || filter.company
    if (filter.branch || branch) params.branchId = branch || filter.branch
    else if (isSuperCompanyUser && companyBranchFilterId) params.branchId = companyBranchFilterId
    if (filter.status || status) params.status = status || filter.status
    if (filter.fuelPolicy) params.fuelPolicy = filter.fuelPolicy
    if (filter.marketSegment) params.marketSegment = filter.marketSegment
    if (filter.insurance) params.insurance = filter.insurance
    if (filter.modelYear) params.modelYear = filter.modelYear
    if (filter.showPendingImagesOnly) params.showPendingImagesOnly = true
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }

    return params
  }

  const fetchCars = async (page = 1, company = null, branch = null, status = null) => {
    const isInitialPage = page === 1
    if (isInitialPage) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }
    try {
      const params = buildCarsParams(page, company, branch, status, true)

      const response = await apiGet('cars', params)

      setCars(isInitialPage ? response.data.rentalCars : [...cars, ...response.data.rentalCars])
      setPagination(response?.data?.pagination || [])
    } finally {
      if (isInitialPage) {
        setLoading(false)
      } else {
        setLoadingMore(false)
      }
    }
  }

  const exportCarsToExcel = async () => {
    try {
      setIsExporting(true)
      const params = buildCarsParams(1, null, null, null, false)
      params.export = true

      const queryParams = new URLSearchParams()
      forEach(params, (value, key) => {
        if (!isNil(value) && value !== '') {
          queryParams.append(key, value)
        }
      })

      const fileBlob = await apiGetBlob(`cars?${queryParams.toString()}`)
      if (!fileBlob) return

      const fileUrl = window.URL.createObjectURL(fileBlob)
      const downloadLink = document.createElement('a')
      downloadLink.href = fileUrl
      downloadLink.download = 'rental-cars.xlsx'
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      window.URL.revokeObjectURL(fileUrl)
    } catch (error) {
      notify.error(t('cars.messages.exportFailed') || 'Failed to export cars')
    } finally {
      setIsExporting(false)
    }
  }

  const copyText = text => {
    navigator.clipboard.writeText(text).then(() => {
      notify.success(t('cars.messages.copiedToClipboard') || 'Copied to clipboard')
    })
  }

  const handleAddCarSuccess = carId => {
    fetchCars()
    closeModal()
  }

  const handleBookingSuccess = () => {
    fetchCars()
    closeBookingModal()
    notify.success(t('cars.messages.carBookedSuccessfully'))
  }

  const [SmsAuthModal, openSmsModel, closeModal] = useModal({
    content: <CarAuth onSuccess={handleAddCarSuccess} />,
    title: t('cars.buttons.addCar'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '800px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [ImportUserModal, openImportUserModal] = useModal({
    content: <ImportUsers refetch={fetchCars} companyNames={companyNames} />,
    title: t('cars.buttons.importCars'),
    closeable: true,
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [BookingModal, openBookingModal, closeBookingModal] = useModal({
    content: <CarBooking carId={selectedCarId} onSuccess={handleBookingSuccess} />,
    title: t('cars.buttons.bookCar'),
    closeable: true,
    customClass: 'overflow-visible',
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const handleBookCar = carId => {
    setSelectedCarId(carId)
    openBookingModal()
  }

  useEffect(() => {
    if (hasAdminAccess) {
      fetchCompanies()
      fetchBranches()
    }
  }, [hasAdminAccess])

  useEffect(() => {
    if (hasAdminAccess) {
      fetchBranches(filter.company)
    }
  }, [filter.company, hasAdminAccess])

  const fetchCompanies = async () => {
    const response = await apiGet('companies', { pageSize: 1000 })
    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
  }

  const fetchBranches = async (companyId = null) => {
    let endpoint = 'branches'

    if (companyId) {
      endpoint = `company/${companyId}/branches`
    }

    const response = await apiGet(endpoint)
    if (response?.data) {
      let branches = []
      if (companyId) {
        branches = response.data.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
      } else {
        branches = response.data.branch.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
      }
      setBranchNames(branches)
    } else {
      setBranchNames([])
    }
  }

  const fetchFuelPolicies = async () => {
    try {
      const response = await apiGet('fuel-policies', { pageSize: 500, pageNumber: 1 })
      const list =
        response?.data?.fuelPolicy?.map(item => ({
          id: item.fuelPolicy,
          name: isArabic ? item.fuelPolicyAr || item.fuelPolicy : item.fuelPolicy,
        })) || []
      setFuelPolicyOptions(list)
    } catch {
      setFuelPolicyOptions([])
    }
  }

  const fetchMarketSegments = async () => {
    try {
      const response = await apiGet('market-segments', { pageSize: 500, pageNumber: 1 })
      const list =
        response?.data?.marketSegment?.map(item => ({
          id: item.marketSegment,
          name: isArabic ? item.marketSegmentAr || item.marketSegment : item.marketSegment,
        })) || []
      setMarketSegmentOptions(list)
    } catch {
      setMarketSegmentOptions([])
    }
  }

  const fetchInsurances = async () => {
    try {
      const response = await apiGet('insurances', { pageSize: 500, pageNumber: 1 })
      const list =
        response?.data?.insurance?.map(item => ({
          id: item.insurance,
          name: isArabic ? item.insuranceAr || item.insurance : item.insurance,
        })) || []
      setInsuranceOptions(list)
    } catch {
      setInsuranceOptions([])
    }
  }

  useEffect(() => {
    fetchFuelPolicies()
    fetchMarketSegments()
    fetchInsurances()
  }, [isArabic])

  const handleCompanyTypeChange = selectedOption => {
    setFilter(prevFilter => {
      const updated = {
        ...prevFilter,
        company: prevFilter.company === selectedOption?.id ? null : selectedOption?.id,
        branch: null,
      }
      return updated
    })
  }

  const handleBranchTypeChange = selectedOption => {
    setFilter(prevFilter => {
      const updated = {
        ...prevFilter,
        branch: prevFilter.branch === selectedOption?.id ? null : selectedOption?.id,
      }
      return updated
    })
  }

  const handleVisibilityChange = async (carId, newVisibilityStatus) => {
    try {
      const response = await apiPut(`car/status/${carId}`, {
        carVisibilityStatus: newVisibilityStatus,
      })

      if (response?.success) {
        setCars(prevCars => prevCars.map(car => (car._id === carId ? { ...car, carVisibilityStatus: newVisibilityStatus } : car)))
        notify.success(t('cars.messages.carVisibilityUpdated'))
      }
    } catch (error) {
      notify.error(t('cars.messages.failedToUpdateVisibility'))
    }
  }

  const getCarStatusLabel = car => {
    const s = toLower(car?.status || '')
    if (s === 'active') return t('cars.status.active')
    if (s === 'booked') return t('cars.status.booked')
    if (s === 'out of service') return t('cars.status.outOfService')
    if (s === 'inprogress') return t('cars.status.inProgress')
    return car?.status || '—'
  }

  const CarCard = ({ car }) => {
    const makeName = isArabic ? car?.car?.make?.makeAr || car?.car?.make?.make : car?.car?.make?.make
    const modelName = isArabic ? car?.car?.model?.modalAr || car?.car?.model?.modal : car?.car?.model?.modal
    const carTitle = trim(`${makeName || ''} ${modelName || ''} ${car?.modelYear || ''}`)
    const segment = isArabic ? car?.car?.marketSegmentAr || car?.car?.marketSegment : car?.car?.marketSegment
    const fuelPolicy = isArabic ? car?.fuelPolicyAr || car?.fuelPolicy : car?.fuelPolicy
    const insurance = isArabic ? car?.insuranceAr || car?.insurance : car?.insurance
    const branchName = car.branch?.[0]?.name
    const createdTime = get(car, 'createdAt', '')

    const s = toLower(car?.status || '')
    const statusBadgeClass = classNames('px-2 py-0.5 text-[9px] font-bold rounded-full uppercase tracking-wide max-w-full truncate ring-1 ring-inset', {
      'bg-emerald-100 text-emerald-900 ring-emerald-200/70': s === 'active',
      'bg-sky-100 text-sky-900 ring-sky-200/70': s === 'booked',
      'bg-rose-100 text-rose-900 ring-rose-200/80': s === 'out of service',
      'bg-amber-50 text-amber-900 ring-amber-200/70': s === 'inprogress',
      'bg-slate-100 text-slate-700 ring-slate-200/80': !includes(['active', 'booked', 'out of service', 'inprogress'], s),
    })

    const MetaCell = ({ label, children, className }) => (
      <div className={classNames('min-w-0', className)}>
        <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
        <div className="truncate text-xs font-semibold leading-tight text-slate-900">{children}</div>
      </div>
    )

    return (
      <div className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm transition-all duration-300 hover:border-slate-300/80 hover:shadow-md">
        <div className="relative h-32 w-full bg-slate-100 sm:h-36">
          {car?.carImages?.[0]?.url ? (
            <img className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]" src={car.carImages[0].url} alt="" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200/90">
              <div
                className="flex h-[4.75rem] w-[4.75rem] items-center justify-center rounded-2xl bg-white/70 shadow-inner ring-1 ring-slate-200/90 sm:h-[5.25rem] sm:w-[5.25rem]"
                aria-hidden
              >
                <TruckIcon className="h-11 w-11 text-slate-400 sm:h-12 sm:w-12" strokeWidth={1.25} />
              </div>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/45 to-transparent" aria-hidden />
          <div className="absolute right-2 top-2 max-w-[calc(100%-1rem)]">
            <span className={statusBadgeClass} title={getCarStatusLabel(car)}>
              {getCarStatusLabel(car)}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2.5 p-3.5 sm:p-4">
          <div className="space-y-0.5">
            <h3 className="truncate text-base font-extrabold leading-snug tracking-tight text-slate-900 transition-colors group-hover:text-dark-600">{carTitle}</h3>
            <div className="font-mono text-[10px] font-bold text-slate-500">ID: {String(car._id || '').slice(-8)}</div>
          </div>

          <div className="rounded-lg bg-slate-50/90 p-2.5 ring-1 ring-slate-200/60">
            <div className="grid grid-cols-2 gap-2 gap-y-1.5">
              {(!isCompanyRole || hasAdminAccess) && car.company?.[0]?.name ? (
                <MetaCell className="col-span-2" label={t('cars.columns.companyName')}>
                  {car.company[0].name}
                </MetaCell>
              ) : null}
              <MetaCell label={t('cars.card.branch')}>{branchName || '—'}</MetaCell>
              <MetaCell className="text-end" label={t('cars.columns.registrationNumber')}>
                <span className="font-mono">{car.registrationNumber || '—'}</span>
              </MetaCell>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-2 gap-y-2 border-t border-slate-200/80 pt-2.5 sm:grid-cols-3">
            <MetaCell label={t('cars.card.segment')}>{segment || '—'}</MetaCell>
            <MetaCell label={t('cars.card.fuelPolicy')}>{fuelPolicy || '—'}</MetaCell>
            <MetaCell label={t('cars.card.insurance')}>{insurance || '—'}</MetaCell>
            <MetaCell label={t('cars.columns.status')}>
              <span className="font-bold text-dark-600">{getCarStatusLabel(car)}</span>
            </MetaCell>
            <MetaCell className="col-span-2 sm:col-span-2" label={t('cars.columns.created')}>
              {createdTime ? moment(createdTime).fromNow() : '—'}
            </MetaCell>
          </div>

          <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200/80 bg-white px-2.5 py-2 ring-1 ring-slate-100">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-600">{t('cars.card.visibility')}</span>
            <Switch value={car.carVisibilityStatus || false} onChange={newVisibilityStatus => handleVisibilityChange(car._id, newVisibilityStatus)} />
          </div>

          <div className="mt-auto grid grid-cols-2 gap-1.5 border-t border-slate-200/80 pt-2.5">
            <button
              type="button"
              onClick={() => history.push(`/car/edit/${car._id}/car-profile${location.search}`)}
              className="rounded-md bg-dark-600 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-white transition-colors hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-1"
            >
              {t('cars.buttons.viewDetails')}
            </button>
            <button
              type="button"
              onClick={() => handleBookCar(car._id)}
              className="rounded-md bg-slate-100 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-slate-800 transition-colors hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:ring-offset-1"
            >
              {t('cars.buttons.bookCar')}
            </button>
            <button
              type="button"
              onClick={() => {
                const carUrl = getCarUrl(car._id)
                if (!carUrl) {
                  notify.error(t('cars.messages.carUrlNotAvailable') || 'Car URL not available')
                  return
                }
                copyText(carUrl)
              }}
              className="col-span-2 flex items-center justify-center gap-1.5 rounded-md bg-slate-50 px-2.5 py-2 text-[10px] font-bold uppercase tracking-tight text-slate-600 ring-1 ring-slate-200/80 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300/60"
            >
              <LinkIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              {t('cars.card.copyPublicLink')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const columns = [
    {
      name: t('cars.columns.name'),
      width: '10%',
      key: 'name',
      sortable: true,
      rendered: item => {
        const makeName = isArabic ? item?.car?.make?.makeAr || item?.car?.make?.make : item?.car?.make?.make
        const modelName = isArabic ? item?.car?.model?.modalAr || item?.car?.model?.modal : item?.car?.model?.modal
        const carName = [makeName, modelName, item?.modelYear].filter(Boolean).join(' ')
        return (
          <a href={`/car/edit/${item._id}/car-profile${location.search}`} className="flex items-center cursor-pointer no-underline">
            <div className="flex-shrink-0 h-10 w-10">
              {item?.carImages?.[0]?.thumbnailUrl ? (
                <img className="h-10 w-10 rounded-full object-cover" src={item?.carImages?.[0]?.thumbnailUrl} alt="" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center shrink-0" aria-hidden>
                  <TruckIcon className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                </div>
              )}
            </div>
            <div className="ml-4 flex-1">
              <div className="flex items-center gap-2">
                <div className="text-sm text-normal text-gray-900">{carName}</div>
                <button
                  onClick={e => {
                    e.stopPropagation()
                    e.preventDefault()
                    const carUrl = getCarUrl(item._id)
                    if (!carUrl) {
                      notify.error(t('cars.messages.carUrlNotAvailable') || 'Car URL not available')
                      return
                    }
                    copyText(carUrl)
                  }}
                  className="text-[13px] text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                  aria-label="Copy URL"
                >
                  Copy URL
                  <IoCopyOutline className="h-3 w-3" />
                </button>
              </div>
            </div>
          </a>
        )
      },
    },
    {
      width: '10%',
      name: t('cars.columns.registrationNumber'),
      key: 'registrationNumber',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.registrationNumber}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('cars.columns.status'),
      key: 'status',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.status}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('cars.columns.segment'),
      key: 'marketSegment',
      sortable: true,
      rendered: item => {
        const segment = isArabic ? item?.car?.marketSegmentAr || item?.car?.marketSegment : item?.car?.marketSegment
        return (
          <>
            <div>
              <span className="text-[13px]">{segment}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('cars.columns.fuelPolicy'),
      key: 'fuelPolicy',
      sortable: true,
      rendered: item => {
        const fuelPolicy = isArabic ? item?.fuelPolicyAr || item?.fuelPolicy : item?.fuelPolicy
        return (
          <>
            <div>
              <span className="text-[13px]">{fuelPolicy}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('cars.columns.insurance'),
      key: 'insurance',
      sortable: true,
      rendered: item => {
        const insurance = isArabic ? item?.insuranceAr || item?.insurance : item?.insurance
        return (
          <>
            <div>
              <span className="text-[13px]">{insurance}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '5%',
      name: t('cars.columns.companyName'),
      key: 'company',
      sortable: true,
      rendered: item => {
        if (item.company && item.company.length > 0) {
          return <span className=" text-[13px]">{item.company[0].name}</span>
        }
      },
    },
    {
      width: '5%',
      name: t('cars.columns.branch'),
      key: 'branch',
      sortable: true,
      rendered: item => {
        if (item.branch && item.branch.length > 0) {
          return <span className=" text-[13px]">{item?.branch[0]?.name}</span>
        }
      },
    },
    {
      width: '5%',
      name: t('cars.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'Registered'
        return createdAt
      },
    },
    {
      width: '5%',
      name: t('cars.columns.visibilityAction'),
      key: 'carVisibilityStatus',
      sortable: false,
      rendered: item => {
        return (
          <div className="flex items-center space-x-2">
            <Switch value={item.carVisibilityStatus || false} onChange={newVisibilityStatus => handleVisibilityChange(item._id, newVisibilityStatus)} />
          </div>
        )
      },
    },
    {
      width: '5%',
      name: t('cars.columns.actions'),
      key: 'actions',
      sortable: false,
      rendered: item => {
        return (
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="outline"
              onClick={e => {
                e.stopPropagation()
                handleBookCar(item._id)
              }}
            >
              {t('cars.buttons.bookCar')}
            </Button>
          </div>
        )
      },
    },
  ]

  const filterColumns = columnsData => {
    return columnsData.filter(item => {
      if (id && item.key === 'workspace') return false

      if (id && item.key === 'lastLogin') return true
      if (!id && item.key === 'lastLogin') return false

      return true
    })
  }

  return (
    <Layout
      showLogo={!!id}
      breadcrumbs={[{ name: t('cars.breadcrumb'), pathname: '/cars' }]}
      headerRight={
        <div className="flex flex-col lg:flex-row items-start lg:items-center w-full justify-end gap-2 lg:gap-4">
          {/* Mobile/tablet: Filters + Add Car — visible below lg so tablet is not crowded */}
          <div className="flex items-center justify-between w-full lg:hidden gap-2 py-2">
            <Button onClick={() => setShowFiltersDrawer(true)} variant="outline" className="flex items-center gap-2 shrink-0">
              <IoFilter className="h-4 w-4" />
              {t('cars.filters.title') || 'Filters'}
            </Button>
            <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
              <div className="relative rounded-md shadow-sm max-w-[10rem] sm:max-w-[14rem] w-full">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                  <IoSearch className={`${isRTL ? 'ml-3' : 'mr-3'} h-4 w-4 text-gray-400`} aria-hidden="true" />
                </div>
                <input
                  onChange={e => {
                    setSearch(e.target.value)
                  }}
                  value={search}
                  type="text"
                  name="search-mobile"
                  id="search-mobile"
                  className={`block w-full ${isRTL ? 'pr-9' : 'pl-9'} shadow-none text-sm bg-gray-100 border-0 focus:outline-none rounded-sm py-2`}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder={t('cars.search.placeholder')}
                />
              </div>
              <Button onClick={openSmsModel} className="shrink-0 whitespace-nowrap">
                {t('cars.buttons.addCar')}
              </Button>
            </div>
          </div>

          {/* Desktop Layout - lg and up */}
          <div className="hidden lg:flex flex-col lg:flex-row items-start lg:items-center w-full justify-end gap-2 lg:gap-4">
            <div className="flex flex-col lg:flex-row gap-2 lg:gap-3 w-full lg:w-auto">
              <div className="flex items-center gap-3">
                <p className="whitespace-nowrap text-sm">{t('cars.filters.needAction')}</p>
                <Switch
                  value={filter.showPendingImagesOnly || false}
                  onChange={e => {
                    setCars([])
                    setFilter(prevFilter => {
                      const updated = { ...prevFilter, showPendingImagesOnly: e }
                      return updated
                    })
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4 w-full lg:w-auto">
              {(!isCompanyRole || hasAdminAccess) && (
                <Selection
                  value={filter.company}
                  onChange={e => {
                    setCars([])
                    handleCompanyTypeChange(e)
                  }}
                  className="min-w-[200px] w-full lg:w-auto"
                  list={companyNames}
                  title={companyTypeFilterTitle}
                  isRTL={isRTL}
                />
              )}
              {(!isCompanyRole || hasAdminAccess) && (
                <Selection
                  value={filter.branch}
                  onChange={e => {
                    setCars([])
                    handleBranchTypeChange(e)
                  }}
                  className="min-w-[200px] w-full lg:w-auto"
                  list={branchNames}
                  title={branchTypeFilterTitle}
                  isRTL={isRTL}
                />
              )}
              <Selection
                value={filter.modelYear}
                onChange={e => {
                  setCars([])
                  setFilter(prevFilter => {
                    const nextId = e?.id != null ? String(e.id) : null
                    return {
                      ...prevFilter,
                      modelYear: prevFilter.modelYear === nextId ? null : nextId,
                    }
                  })
                }}
                className="min-w-[120px] w-full lg:w-auto"
                list={modelYearOptions}
                title={modelYearFilterTitle}
                isRTL={isRTL}
              />
              <Button onClick={() => setShowFiltersDrawer(true)} variant="outline" className="flex items-center gap-2">
                <IoFilter className="h-4 w-4" />
                {t('cars.filters.more') || 'More Filter'}
              </Button>
            </div>

            <div className="flex items-center gap-2 w-full lg:w-auto">
              <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
              <div className="mt-1 relative rounded-md shadow-sm flex-1 lg:flex-none">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                  <IoSearch className={`${isRTL ? 'ml-3' : 'mr-3'} h-4 w-4 text-gray-400`} aria-hidden="true" />
                </div>
                <input
                  onChange={e => {
                    setSearch(e.target.value)
                  }}
                  value={search}
                  type="text"
                  name="search"
                  id="search"
                  className={`block w-full ${isRTL ? 'pr-9' : 'pl-9'} shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm`}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  placeholder={t('cars.search.placeholder')}
                />
              </div>
            </div>

            {/* Action Buttons — admin also gets Add Car here so it is not FAB-only */}
            <div className="flex flex-col lg:flex-row gap-2 w-full lg:w-auto">
              {!hasAdminAccess && !isCompanyRole && !isSuperCompanyUser && (
                <Button onClick={openImportUserModal} className="w-full lg:w-auto">
                  {t('cars.buttons.import')}
                </Button>
              )}
              <Button onClick={openSmsModel} className="w-full lg:w-auto">
                {t('cars.buttons.addCar')}
              </Button>
            </div>
          </div>
        </div>
      }
      title={t('cars.title')}
    >
      {/* Mobile Filters Drawer */}
      <Transition.Root show={showFiltersDrawer} as={Fragment}>
        <Dialog as="div" className="relative z-[11000]" onClose={setShowFiltersDrawer}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                      <div className="px-4 sm:px-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-lg font-medium text-gray-900">{t('cars.filters.title') || 'Filters'}</Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              onClick={() => setShowFiltersDrawer(false)}
                            >
                              <span className="sr-only">Close panel</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="relative mt-6 flex-1 px-4 sm:px-6">
                        <div className="space-y-6">
                          {/* Mobile/tablet-only drawer controls */}
                          <div className="lg:hidden flex items-center justify-between">
                            <p className="text-sm text-gray-700">{t('cars.filters.needAction')}</p>
                            <Switch
                              value={filter.showPendingImagesOnly || false}
                              onChange={e => {
                                setCars([])
                                setFilter(prevFilter => {
                                  const updated = { ...prevFilter, showPendingImagesOnly: e }
                                  return updated
                                })
                              }}
                            />
                          </div>

                          {/* Company Filter */}
                          {(!isCompanyRole || hasAdminAccess) && (
                            <div className="lg:hidden">
                              <label className="block text-sm font-medium text-gray-700 mb-2">{t('cars.filters.company') || 'Company'}</label>
                              <Selection
                                value={filter.company}
                                onChange={e => {
                                  setCars([])
                                  handleCompanyTypeChange(e)
                                }}
                                className="w-full"
                                list={companyNames}
                                title={companyTypeFilterTitle}
                                isRTL={isRTL}
                              />
                            </div>
                          )}

                          {/* Branch Filter */}
                          {(!isCompanyRole || hasAdminAccess) && (
                            <div className="lg:hidden">
                              <label className="block text-sm font-medium text-gray-700 mb-2">{t('cars.filters.branch') || 'Branch'}</label>
                              <Selection
                                value={filter.branch}
                                onChange={e => {
                                  setCars([])
                                  handleBranchTypeChange(e)
                                }}
                                className="w-full"
                                list={branchNames}
                                title={branchTypeFilterTitle}
                                isRTL={isRTL}
                              />
                            </div>
                          )}

                          {/* Status Filter (visible both mobile and desktop drawer) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{carStatusFilterTitle}</label>
                            <Selection
                              value={filter.status}
                              onChange={e => {
                                setCars([])
                                setFilter(prevFilter => {
                                  const updated = {
                                    ...prevFilter,
                                    status: prevFilter.status === e?.id ? null : e?.id,
                                  }
                                  return updated
                                })
                              }}
                              className="w-full"
                              list={RentalCarStatus}
                              title={carStatusFilterTitle}
                              isRTL={isRTL}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{modelYearFilterTitle}</label>
                            <Selection
                              value={filter.modelYear}
                              onChange={e => {
                                setCars([])
                                setFilter(prevFilter => {
                                  const nextId = e?.id != null ? String(e.id) : null
                                  return {
                                    ...prevFilter,
                                    modelYear: prevFilter.modelYear === nextId ? null : nextId,
                                  }
                                })
                              }}
                              className="w-full"
                              list={modelYearOptions}
                              title={modelYearFilterTitle}
                              isRTL={isRTL}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{fuelPolicyFilterTitle}</label>
                            <Selection
                              value={filter.fuelPolicy}
                              onChange={e => {
                                setCars([])
                                setFilter(prevFilter => ({
                                  ...prevFilter,
                                  fuelPolicy: prevFilter.fuelPolicy === e?.id ? null : e?.id,
                                }))
                              }}
                              className="w-full"
                              list={fuelPolicyOptions}
                              title={fuelPolicyFilterTitle}
                              isRTL={isRTL}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{marketSegmentFilterTitle}</label>
                            <Selection
                              value={filter.marketSegment}
                              onChange={e => {
                                setCars([])
                                setFilter(prevFilter => ({
                                  ...prevFilter,
                                  marketSegment: prevFilter.marketSegment === e?.id ? null : e?.id,
                                }))
                              }}
                              className="w-full"
                              list={marketSegmentOptions}
                              title={marketSegmentFilterTitle}
                              isRTL={isRTL}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{insuranceFilterTitle}</label>
                            <Selection
                              value={filter.insurance}
                              onChange={e => {
                                setCars([])
                                setFilter(prevFilter => ({
                                  ...prevFilter,
                                  insurance: prevFilter.insurance === e?.id ? null : e?.id,
                                }))
                              }}
                              className="w-full"
                              list={insuranceOptions}
                              title={insuranceFilterTitle}
                              isRTL={isRTL}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <div className={classNames('flex flex-col', hasAdminAccess ? 'pb-24 lg:pb-24 2xl:pb-16' : 'pb-8 lg:pb-16 2xl:pb-0')}>
        <div className="flex-1 flex items-stretch min-w-0">
          <main className="flex-1 min-w-0 w-full">
            <div className="w-full max-w-full">
              <div className="w-full max-w-full">
                <CompanyStatsSummary
                  variant="cars"
                  reloadKey={`${pagination.currentPage}-${pagination.totalRecords}-${filter.status || ''}-${filter.modelYear || ''}-${filter.fuelPolicy || ''}-${
                    filter.marketSegment || ''
                  }-${filter.insurance || ''}-${filter.branch || ''}-${filter.company || ''}`}
                />
                {!loading ? (
                  <>
                    {cars?.length === 0 ? (
                      <EmptyState v2 description="No Cars found" />
                    ) : (
                      <>
                        {/* Cards on mobile/tablet (and always for company users) — visibility via CSS media queries */}
                        <div className={classNames(isCompanyRole && !hasAdminAccess ? 'block' : 'lg:hidden')}>
                          <div className="grid w-full grid-cols-1 gap-4 px-4 pb-6 pt-1 sm:px-5 sm:pt-3 lg:grid-cols-2 lg:gap-5 lg:pb-4 2xl:grid-cols-3">
                            {cars.map(car => (
                              <div key={car._id} className="min-w-0">
                                <CarCard car={car} />
                              </div>
                            ))}
                          </div>
                          {pagination?.currentPage !== pagination?.totalPages ? (
                            <div className="relative z-[1200] mt-4 mb-8 flex justify-center px-4 lg:mb-12 2xl:mb-4">
                              <button
                                type="button"
                                onClick={() => fetchCars(pagination?.currentPage + 1)}
                                className="inline-flex min-w-[10rem] mb-10 items-center justify-center gap-2 rounded-md bg-dark-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={loadingMore}
                              >
                                {loadingMore ? (
                                  <>
                                    <LoadingIcon className="h-5 w-5" />
                                    <span>{t('common.loading')}</span>
                                  </>
                                ) : (
                                  <span>{t('common.loadMore')}</span>
                                )}
                              </button>
                            </div>
                          ) : null}
                        </div>
                        {/* Desktop table for admin / non-company roles — shown from lg breakpoint up */}
                        {!(isCompanyRole && !hasAdminAccess) && (
                          <div className="hidden lg:block w-full max-w-full overflow-x-auto">
                            <Table
                              hasMore={pagination?.currentPage !== pagination?.totalPages}
                              fetchData={() => fetchCars(pagination?.currentPage + 1)}
                              sortBy={filter.sortBy}
                              sortOrder={filter.sortOrder}
                              handleSort={(sortBy, sortOrder) => {
                                setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                              }}
                              data={cars}
                              columns={filterColumns(columns)}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      {hasAdminAccess && (
        <Menu
          as="div"
          className={classNames(
            'fixed bottom-6 z-[1250] flex max-w-[calc(100vw-1.5rem)] flex-col gap-2 sm:bottom-8 sm:max-w-none sm:gap-3',
            isRTL ? 'left-6 items-start sm:left-8' : 'right-6 items-end'
          )}
        >
          <Transition
            enter="transition ease-out duration-200"
            enterFrom="translate-y-3 opacity-0 sm:translate-y-4"
            enterTo="translate-y-0 opacity-100"
            leave="transition ease-in duration-150"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="translate-y-2 opacity-0 sm:translate-y-3"
          >
            <Menu.Items
              static
              className={classNames('flex min-w-[12rem] max-w-[min(100vw-2.5rem,18rem)] flex-col gap-2 bg-transparent p-0 shadow-none focus:outline-none', 'border-0 ring-0')}
            >
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    disabled={isExporting}
                    onClick={() => exportCarsToExcel()}
                    className={classNames(
                      'inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full border border-slate-200/90 bg-white px-3.5 py-2.5 text-xs font-semibold text-slate-800 shadow-sm transition-all sm:px-4 sm:text-sm',
                      'hover:border-slate-300 hover:bg-slate-50 hover:shadow',
                      active && 'border-slate-300 bg-slate-50',
                      isExporting && 'cursor-wait opacity-70'
                    )}
                  >
                    <DocumentDownloadIcon className="h-4 w-4 shrink-0 text-slate-600 sm:h-5 sm:w-5" strokeWidth={2} aria-hidden />
                    <span className="whitespace-nowrap">{isExporting ? t('common.loading') : t('cars.buttons.exportToExcel')}</span>
                  </button>
                )}
              </Menu.Item>
              {!isSuperCompanyUser && (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => openImportUserModal()}
                      className={classNames(
                        'inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full border border-emerald-200/90 bg-emerald-50/90 px-3.5 py-2.5 text-xs font-semibold text-emerald-900 shadow-sm transition-all sm:px-4 sm:text-sm',
                        'hover:border-emerald-300 hover:bg-emerald-100 hover:shadow',
                        active && 'border-emerald-300 bg-emerald-100'
                      )}
                    >
                      <UploadIcon className="h-4 w-4 shrink-0 text-emerald-700 sm:h-5 sm:w-5" strokeWidth={2} aria-hidden />
                      <span className="whitespace-nowrap">{t('cars.buttons.import')}</span>
                    </button>
                  )}
                </Menu.Item>
              )}
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => openSmsModel()}
                    className={classNames(
                      'inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full border border-dark-600/20 bg-dark-600 px-3.5 py-2.5 text-xs font-semibold text-white shadow-md transition-all sm:px-4 sm:text-sm',
                      'hover:border-dark-700 hover:bg-dark-700 hover:shadow-lg',
                      active && 'bg-dark-700'
                    )}
                  >
                    <PlusIcon className="h-4 w-4 shrink-0 text-white sm:h-5 sm:w-5" strokeWidth={2} aria-hidden />
                    <span className="whitespace-nowrap">{t('cars.buttons.addCar')}</span>
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
          <Menu.Button
            type="button"
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-dark-600 text-white shadow-lg transition-all hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2"
            aria-label={t('cars.actions.moreActions')}
            title={t('cars.actions.moreActions')}
          >
            <DotsVerticalIcon className="h-7 w-7" strokeWidth={1.75} aria-hidden />
          </Menu.Button>
        </Menu>
      )}

      <SmsAuthModal />
      <ImportUserModal />
      <BookingModal />
    </Layout>
  )
}

export default Cars
